import React from "react";
import { CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "theme_mode";

export const ColorModeContext = React.createContext<{
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}>({
  mode: "light",
  setMode: () => undefined,
  toggleMode: () => undefined,
});

function getInitialMode(): ThemeMode {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;

  if (typeof window !== "undefined" && window.matchMedia) {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  }

  return "light";
}

export default function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<ThemeMode>(() => getInitialMode());

  const setMode = React.useCallback((next: ThemeMode) => {
    setModeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const toggleMode = React.useCallback(() => {
    setMode(mode === "light" ? "dark" : "light");
  }, [mode, setMode]);

  const theme = React.useMemo(
    () => {
      const darkShadows = [
        "none",
        "0px 1px 2px rgba(0,0,0,0.45)",
        "0px 2px 8px rgba(0,0,0,0.55)",
        "0px 6px 16px rgba(0,0,0,0.60)",
        ...Array.from({ length: 21 }, () => "0px 10px 28px rgba(0,0,0,0.65)"),
      ] as any;

      const themeOptions = {
        shape: {
          borderRadius: 12,
        },
        typography: {
          fontFamily: [
            "Inter",
            "system-ui",
            "-apple-system",
            "Segoe UI",
            "Roboto",
            "Arial",
            "sans-serif",
          ].join(","),
        },
        palette: {
          mode,
          ...(mode === "dark"
            ? {
                background: {
                  default: "#0f1115",
                  paper: "#151a22",
                },
                primary: {
                  main: "#7c5cff",
                },
                secondary: {
                  main: "#2dd4bf",
                },
                error: {
                  main: "#ff5c7a",
                },
                warning: {
                  main: "#fbbf24",
                },
                success: {
                  main: "#22c55e",
                },
                divider: "rgba(255,255,255,0.10)",
              }
            : {
                background: {
                  default: "#f6f7fb",
                  paper: "#ffffff",
                },
                primary: {
                  main: "#4f46e5",
                },
                secondary: {
                  main: "#0ea5e9",
                },
                divider: "rgba(0,0,0,0.08)",
              }),
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
              },
              "input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill": {
                WebkitBoxShadow:
                  mode === "dark"
                    ? "0 0 0px 1000px #151a22 inset"
                    : "0 0 0px 1000px #ffffff inset",
                WebkitTextFillColor: mode === "dark" ? "#ffffff" : "#111827",
                caretColor: mode === "dark" ? "#ffffff" : "#111827",
                transition: "background-color 9999s ease-out 0s",
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: "divider",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                border: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 12,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 999,
              },
            },
          },
          MuiToggleButtonGroup: {
            styleOverrides: {
              root: {
                borderRadius: 999,
                padding: 2,
                border: mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                borderRadius: 999,
                border: 0,
                paddingLeft: 10,
                paddingRight: 10,
                color: mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)",
                "&.Mui-selected": {
                  color: "inherit",
                  backgroundColor: mode === "dark" ? "rgba(124,92,255,0.22)" : "rgba(79,70,229,0.14)",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: mode === "dark" ? "rgba(124,92,255,0.28)" : "rgba(79,70,229,0.18)",
                },
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                height: 3,
                borderRadius: 999,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: "outlined",
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: {
                fontWeight: 700,
                borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
              },
              body: {
                borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
              },
            },
          },
        },
      } as const;

      return createTheme(mode === "dark" ? { ...themeOptions, shadows: darkShadows } : themeOptions);
    },
    [mode]
  );

  React.useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const themeColor = mode === "dark" ? "#0f1115" : "#f6f7fb";
    meta.setAttribute("content", themeColor);
  }, [mode]);

  const value = React.useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
